(ns graph-ui.web.tab
  "WHICH ISLAND THIS BROWSER TAB IS ON, said in the two places a browser tab
   has room to say anything: its name and its icon.

  ;; @feature F-graph-ui-v0-014-01-island-picker
  ;; @adr     ADR-graph-ui-v0-014-01-ui-building-blocks

  George, 2026-09-07: *\"All islands are not distinguishable as browser tabs -
  all carry same favicon and name in the tab.\"* Both halves were literally
  true. `resources/public/web/index.html` carried
  `<title>ikc3 — notes</title>` — one static string, so every island read
  \"notes\", the reader included — and the page declared no icon at all, so the
  browser drew its own blank sheet for all of them.

  ── ONE APPLICATION, MANY ISLANDS, AND ONLY ONE index.html ──────────────────

  This is not a defect ikc1 had, and there is nothing to copy from it. Athens
  was ONE application: `packages/athens/index.html` names one title and one
  `favicon.svg`, correctly, because it had one surface. ikc3 serves every island
  from a single bundle and tells them apart with `?island=`, so the tab's
  identity has to be written at runtime — there is no second HTML file to put it
  in.

  ── THE ICON IS THE BRAND MARK, WHICH MAKES A DECLARED FACT TRUE ────────────

  `dsl/design/brand.edn` has carried `[favicon is not-built yet]` since it was
  written. This builds it, and out of the mark that file already declares rather
  than a new drawing: `resources/public/brand/ikc-mark.svg`, two overlapping
  rounded squares — `[mark is two-islands]`, `[mark shows overlap]`,
  `[canvas can-not end]`.

  AND THE MARK ALREADY MEANS WHICH ISLAND YOU ARE ON. Its own comment says the
  solid square is \"an island you are on\" and that \"the near one is solid
  because that is the one in front\". So the per-island part needs no new idea:
  the island in front takes THIS island's own hue, the outlined one behind it
  stays the neutral canvas running off the edge. The product's mark stays the
  product's mark; the tab says which of its islands is in front.

  ── WHY A LITERAL COLOUR IS ALLOWED HERE, AND NOWHERE ELSE ──────────────────

  `hue` returns an angle, and the icon is drawn in `hsl`. A theme token would be
  the rule anywhere on the canvas, and this is not on the canvas: a favicon is
  painted by the browser, in its own chrome, next to the tabs of unrelated
  sites. It cannot read a CSS custom property — an SVG loaded as a
  `data:` favicon has no access to the document's variables at all, measured the
  first time the mark was handed over verbatim and came back black. So the
  choice is a computed colour or no colour.

  IT IS DERIVED, NOT DECLARED, AND THAT IS THE HALF TO REPLACE. An island's
  identity should say what colour it is; `:ui-islands` answers
  `{:value :label}` and nothing else, so a declared icon would need that source
  widened and the bus restarted. Until then the name is the input, which has one
  real virtue: it needs no registry, so an island added tomorrow gets a
  distinct tab with no edit here."
  (:require [clojure.string :as str]))

(def ^:private product
  "The wordmark, from `dsl/design/brand.edn`: `[wordmark is \"ikc3\"]`."
  "ikc3")

(defn title
  "PURE. What the tab is called.

   THE ISLAND FIRST, because a browser truncates from the right and pinned tabs
   show almost nothing. \"reader-v3 · ikc3\" survives the squeeze as
   \"reader-v3…\"; \"ikc3 — reader-v3\" survives as \"ikc3…\", which is the
   string every tab already shared and the reason this function exists.

   A MIDDLE DOT, not an em dash. The dash was in the static title and reads as a
   sentence break; the tab is two labels, not a phrase."
  [island]
  (let [i (str/trim (str island))]
    (if (str/blank? i) product (str i " · " product))))

(defn hue
  "PURE. An angle on the colour wheel for one island name, 0–359.

   A SUM OF CHARACTER CODES TIMES A PRIME, which is deliberately the dullest
   hash available. Two properties are wanted and no others: the same island is
   the same colour on every machine and after every reload, and two islands are
   usually not the same colour. Nothing here is a secret, and a better
   distribution would still be a guess about names nobody has written yet.

   THE STEP IS 37 DEGREES rather than a free angle. 37 and 360 share no factor,
   so the multiples walk all the way around the wheel before any repeat, and one
   character of difference in a name moves the hue a long way. `reader-v2` and
   `reader-v3` are the names that matter here: at 16 pixels, two hues eight
   degrees apart are one colour.

   THE ACCUMULATOR IS KEPT UNDER A PRIME, and that is not decoration. JavaScript
   has one number type, so `acc * 31` for a name of any length leaves the exact
   integer range and starts rounding — the result is still the same everywhere,
   because the arithmetic is deterministic, but it stops being the function this
   docstring describes. A modulus each step keeps every value exact."
  [island]
  (let [s (str island)
        n (reduce (fn [acc c] (mod (+ (* acc 31) (.charCodeAt c 0)) 100000007))
                  7 (seq s))]
    (mod (* 37 n) 360)))

(defn mark-svg
  "PURE. The brand mark as an SVG string, with the near island in `h`.

   THE GEOMETRY IS `resources/public/brand/ikc-mark.svg`, VERBATIM — the same
   two rects, the same 24-unit box, the same radii and stroke width. It is
   copied rather than fetched because a favicon must be ready before the first
   paint, and a fetch is a race the tab loses on a cold load. IF THAT FILE'S
   SHAPE CHANGES, THIS STRING IS THE SECOND PLACE TO CHANGE, which is the cost
   of the copy and is written down here so the next reader finds it.

   `currentColor` and `var(--accent)` are both resolved to literals, because
   neither reaches an SVG the browser loads as a `data:` icon.

   THE SECOND SQUARE STAYS NEUTRAL AND UNFILLED. It is the canvas running off
   the artboard, which is the one thing about the mark that is true of every
   island — colouring it too would say the canvas belongs to the island."
  [h]
  (str "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'>"
       "<rect x='2.5' y='2.5' width='12' height='12' rx='3' "
       "fill='hsl(" h " 62% 48%)'/>"
       "<rect x='9' y='9' width='14.5' height='14.5' rx='3' fill='none' "
       "stroke='hsl(" h " 30% 62%)' stroke-width='1.75'/>"
       "</svg>"))

(defn data-uri
  "PURE. `mark-svg` as an `image/svg+xml` data URI.

   ENCODED WITH encodeURIComponent AND NOT base64. The payload is a few hundred
   bytes of ASCII markup, so percent-encoding keeps it readable in devtools;
   `btoa` would also throw on any non-Latin-1 byte, which a future mark could
   easily hold."
  [island]
  (str "data:image/svg+xml,"
       (js/encodeURIComponent (mark-svg (hue island)))))

(defn- link-el
  "The one <link rel=icon> this document has, made if it is not there yet.

   REUSED RATHER THAN APPENDED. Called again on every island change, and a
   browser honours the LAST icon link — so appending would leave a growing pile
   of dead nodes and, in Safari, the first one winning."
  []
  (or (.querySelector js/document "link#ikc-favicon")
      (let [el (js/document.createElement "link")]
        (set! (.-id el) "ikc-favicon")
        (set! (.-rel el) "icon")
        (set! (.-type el) "image/svg+xml")
        (.appendChild (.-head js/document) el)
        el)))

(defn identify!
  "Name this tab and give it its icon. Safe to call again with the same island.

   THE ISLAND COMES FROM `?island=`, AND THERE IS NOWHERE ELSE TO GET IT. The
   attach reply is the tempting second source and it does not carry one:
   `ui.attach`'s own contract answers `:island-id`, `:presentation-id`,
   `:widget-tree`, `:sources` and `:meta-ui-rules`, and no title (read
   2026-09-07, rather than assumed). The URL is also the right source anyway —
   it is what `core/island-from-url` reads, what the picker links by, and what a
   person sees in the address bar.

   SO IT IS CALLED AT BOOT, BEFORE THE SOCKET OPENS. A tab that is still
   connecting is already labelled, and a tab that never connects at all is still
   identifiable, which is when a person most wants to know which of six tabs
   they are looking at.

   ONE CONSEQUENCE, STATED RATHER THAN HIDDEN: a `?island=` naming an island
   this deployment does not hold still labels the tab with that name. The tab
   then says what was ASKED for while the page shows the attach error — which is
   the honest pairing, and better than a tab that stays anonymous.

   NOTHING IS WRITTEN FOR A BLANK ISLAND. Overwriting a correct title with the
   product name alone would be a step backwards, and `nil` reaches here
   whenever an attach fails."
  [island]
  (when-not (str/blank? (str island))
    (try
      (set! (.-title js/document) (title island))
      (set! (.-href (link-el)) (data-uri island))
      ;; A TAB THAT CANNOT BE LABELLED IS NOT A BROKEN TAB. Nothing above is
      ;; load-bearing for the application, and a thumbnail capture or a
      ;; sandboxed frame can refuse the head write — so this must never be the
      ;; reason an island fails to open.
      (catch :default e
        (js/console.warn "tab: could not set the tab's identity" e)))))
